import { createContext, FC, JSX, useEffect, useState } from "react";
import { UploadExceptionsWizardContextSteps } from "@app/exceptions/contexts/interfaces";
import { InvalidItemsTableData, ResultsSummaryTableData } from "@app/shared/provider-change-wizards/interfaces";
import { SpecialtyGroupException } from "@app/exceptions/interfaces";

interface UploadExceptionsContextProps {
  bulkUploadFormData: FormData;
  error: string | JSX.Element;
  invalidItems: InvalidItemsTableData[];
  loading: boolean;
  page: UploadExceptionsWizardContextSteps;
  resultsSummary: ResultsSummaryTableData[];
  totalOverwrittenExceptions: number;
  setBulkUploadFormData: (FormData: FormData) => void;
  setError: React.Dispatch<React.SetStateAction<string | JSX.Element>>;
  setInvalidItems: (invalidItems: InvalidItemsTableData[]) => void;
  setPage: React.Dispatch<React.SetStateAction<UploadExceptionsWizardContextSteps>>;
  setResultsSummary: (results: ResultsSummaryTableData[]) => void;
  setShowChangeWarning: React.Dispatch<React.SetStateAction<boolean>>;
  setTotalOverwrittenExceptions: React.Dispatch<React.SetStateAction<number>>;
  setValidRecords: (validRecords: SpecialtyGroupException[]) => void;
  showChangeWarning: boolean;
  validRecords: SpecialtyGroupException[];
}

const UploadExceptionsContext = createContext<Partial<UploadExceptionsContextProps>>({
  invalidItems: [],
  resultsSummary: [],
  setInvalidItems: () => null,
  setResultsSummary: () => null,
  setTotalOverwrittenExceptions: () => null,
  setValidRecords: () => null,
  totalOverwrittenExceptions: 0,
  validRecords: [],
});

const UploadExceptionsProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [bulkUploadFormData, setBulkUploadFormData] = useState<FormData>(null);
  const [error,  setError] = useState<string | JSX.Element>("");
  const [invalidItems, setInvalidItems] = useState<InvalidItemsTableData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState<UploadExceptionsWizardContextSteps>(null);
  const [resultsSummary, setResultsSummary] = useState<ResultsSummaryTableData[]>([]);
  const [showChangeWarning, setShowChangeWarning] = useState(false);
  const [validRecords, setValidRecords] = useState<SpecialtyGroupException[]>([]);
  const [totalOverwrittenExceptions, setTotalOverwrittenExceptions] = useState<number>(0);

  useEffect(() => {
    setPage(UploadExceptionsWizardContextSteps.Upload);
    setLoading(false);
  }, []);

  const contextValue: UploadExceptionsContextProps = {
    bulkUploadFormData,
    error,
    invalidItems,
    loading,
    page,
    resultsSummary,
    setBulkUploadFormData,
    setError,
    setInvalidItems,
    setPage,
    setResultsSummary,
    setShowChangeWarning,
    setTotalOverwrittenExceptions,
    setValidRecords,
    showChangeWarning,
    totalOverwrittenExceptions,
    validRecords,
  };

  return (
    <UploadExceptionsContext.Provider value={contextValue}>
      {props.children}
    </UploadExceptionsContext.Provider>
  );
};

export { UploadExceptionsContext, UploadExceptionsProvider }